import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, FlaskConical, Share2, Save, AlertTriangle, Beaker, Syringe, Repeat, ChevronDown, ChevronUp, User, Target, Dumbbell, Calendar, Shield, Moon, Brain, Flame, HeartPulse, Sparkles, Zap, BookOpen, ExternalLink, Info, ShoppingCart } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { trackAffiliateClick } from '../lib/analytics';
import { AprTrustBlock } from '../components/apr/AprTrustBlock';

// ═══════════════════════════════════════════════
// COMPREHENSIVE PEPTIDE DATABASE
// ═══════════════════════════════════════════════

const PEPTIDE_DB = {
  bpc157: {
    id: 'bpc157', name: 'BPC-157', category: 'Recovery & Healing', color: '#E63946',
    affiliateUrl: 'https://www.americanpeptideresearch.net/product/bpc-157-10mg/ref/16/',
    benefits: ['Tendon & ligament repair', 'Muscle recovery', 'Gut healing', 'Anti-inflammatory'],
    tags: ['recovery', 'gut', 'tendon', 'joint', 'injury'],
    dosing: {
      male:   { under30: '250-500 mcg/day', '30-40': '250-500 mcg/day', '40-50': '500 mcg/day', over50: '500 mcg/day' },
      female: { under30: '250 mcg/day', '30-40': '250-500 mcg/day', '40-50': '250-500 mcg/day', over50: '500 mcg/day' },
    },
    frequency: '1-2x daily', route: 'SubQ (near injury site or abdomen)',
    cycle: '4 weeks on / 2 weeks off', vialMg: 5, waterMl: 2, safetyRating: 5,
    research: 'Extensive preclinical data showing accelerated healing of tendons, muscles, ligaments, and GI mucosa. One of the most studied and safest peptides in clinical use.',
    notes: 'Inject near injury site for localized effect. Abdomen injection for systemic/gut benefits. Can be stacked with virtually everything.',
    articleSlug: 'bpc-157-dosage-guide',
    whyFor: {
      recovery: 'Gold standard for tissue repair — accelerates healing of tendons, muscles, and ligaments by upregulating growth factor expression.',
      gut: 'Heals GI mucosal lining and reduces systemic inflammation — particularly effective for leaky gut and IBS symptoms.',
      joint: 'Directly supports tendon and ligament collagen synthesis, reducing inflammation at the joint level.',
      injury: 'Most-researched peptide for acute injury recovery. Works by promoting angiogenesis and nitric oxide pathways at the injury site.',
      default: 'Foundational recovery peptide that supports tissue repair, gut integrity, and overall anti-inflammatory pathways.',
    },
  },
  tb500: {
    id: 'tb500', name: 'TB-500', category: 'Recovery & Healing', color: '#E63946',
    affiliateUrl: 'https://www.americanpeptideresearch.net/product/tb-500-10mg/ref/16/',
    benefits: ['Systemic tissue repair', 'Reduces inflammation', 'Promotes flexibility', 'Hair regrowth'],
    tags: ['recovery', 'joint', 'injury', 'anti-aging'],
    dosing: {
      male:   { under30: '2-5 mg/week', '30-40': '2.5-5 mg/week', '40-50': '5 mg/week', over50: '5 mg/week' },
      female: { under30: '2 mg/week', '30-40': '2-5 mg/week', '40-50': '2.5-5 mg/week', over50: '5 mg/week' },
    },
    frequency: '2x/week (loading) then 1x/week', route: 'SubQ (anywhere)',
    cycle: '4-6 weeks loading, then maintenance or off', vialMg: 5, waterMl: 2, safetyRating: 4,
    research: 'Thymosin Beta-4 derivative. Promotes cell migration and new blood vessel growth. Well-studied for systemic tissue repair.',
    notes: 'Often stacked with BPC-157 for synergistic healing. Loading phase (4-6 weeks) then optional maintenance or cycle off.',
    articleSlug: 'tb500-heart-health-cardiac-repair',
    whyFor: {
      recovery: 'Complements BPC-157 with systemic reach — promotes cell migration to damaged tissue throughout the body.',
      joint: 'Excellent for chronic tendonitis, hamstrings, knee issues, and plantar fascia through its anti-fibrotic properties.',
      injury: 'Promotes angiogenesis and cell migration for deep tissue repair. The BPC-157 + TB-500 combo is the "Wolverine Stack."',
      default: 'Systemic recovery peptide that complements localized healing agents. Particularly effective for chronic injuries.',
    },
  },
  'cjc-ipa': {
    id: 'cjc-ipa', name: 'CJC-1295 / Ipamorelin', category: 'GH Secretagogue', color: '#00F0FF',
    affiliateUrl: 'https://www.americanpeptideresearch.net/ref/16/',
    benefits: ['Growth hormone release', 'Deep sleep improvement', 'Lean muscle support', 'Fat metabolism', 'Recovery enhancement'],
    tags: ['sleep', 'recovery', 'body-comp', 'anti-aging', 'performance'],
    dosing: {
      male:   { under30: '200-300 mcg/night', '30-40': '300 mcg/night', '40-50': '300-500 mcg/night', over50: '300-500 mcg/night' },
      female: { under30: '100-200 mcg/night', '30-40': '100-200 mcg/night', '40-50': '200-300 mcg/night', over50: '200-300 mcg/night' },
    },
    frequency: '1x/day (before bed)', route: 'SubQ (abdomen)',
    cycle: '12 weeks on / 4 weeks off', vialMg: 5, waterMl: 2.5, safetyRating: 4,
    research: 'CJC-1295 (no DAC) + Ipamorelin is the most widely prescribed GH secretagogue combination. Stimulates natural GH pulsatile release without cortisol or prolactin elevation.',
    notes: 'Take on completely empty stomach (2+ hrs after eating). Best 30 min before bed for synergy with natural GH pulse. Women respond very well to lower doses.',
    articleSlug: 'cjc-1295-with-dac-ipamorelin-how-these-2-peptides-rebuild-your-body-from-the-inside-out',
    whyFor: {
      sleep: 'Dramatically improves sleep depth and quality by amplifying the natural nocturnal GH surge. Users report waking more refreshed within 1-2 weeks.',
      recovery: 'GH drives recovery at the cellular level — enhanced protein synthesis, collagen repair, and reduced exercise-induced inflammation.',
      'body-comp': 'Supports lean muscle gain while promoting lipolysis (fat breakdown), especially around the midsection.',
      'anti-aging': 'Restores youthful GH levels that naturally decline after age 30. Improves skin elasticity, energy, and overall vitality.',
      performance: 'Enhances recovery between training sessions and supports lean mass maintenance during heavy training blocks.',
      default: 'The most popular GH peptide combo for natural, safe growth hormone optimization — improving sleep, recovery, and body composition.',
    },
  },
  motsc: {
    id: 'motsc', name: 'MOTS-C', category: 'Metabolic & Endurance', color: '#00B4D8',
    affiliateUrl: null,
    benefits: ['Mitochondrial energy production', 'Fat utilization during exercise', 'Metabolic flexibility', 'Exercise mimetic effects'],
    tags: ['performance', 'body-comp', 'anti-aging', 'endurance'],
    dosing: {
      male:   { under30: '5-10 mg 2-3x/week', '30-40': '5-10 mg 2-3x/week', '40-50': '10 mg 2-3x/week', over50: '10 mg 2-3x/week' },
      female: { under30: '5 mg 1-2x/week', '30-40': '5-10 mg 1-2x/week', '40-50': '5-10 mg 2x/week', over50: '10 mg 2x/week' },
    },
    frequency: '2-3x/week (cycled)', route: 'SubQ',
    cycle: '4 weeks on / 2 weeks off', vialMg: 10, waterMl: 2, safetyRating: 4,
    research: 'Mitochondrial-derived peptide that activates AMPK pathway. Shows promise as an exercise mimetic — enhancing the metabolic benefits of training.',
    notes: 'Particularly interesting for athletes in their 30s+. Often used during training blocks rather than rest phases. Best on training days.',
    articleSlug: 'mots-c-benefits',
    whyFor: {
      performance: 'Enhances mitochondrial energy production and fat oxidation during exercise — directly improves endurance capacity and metabolic flexibility.',
      'body-comp': 'Activates AMPK (the "metabolic master switch"), increasing fat utilization and improving insulin sensitivity.',
      'anti-aging': 'Counteracts age-related mitochondrial decline. Research shows it can reverse metabolic aging markers in older adults.',
      endurance: 'True exercise mimetic — enhances the cellular adaptations you get from training, particularly mitochondrial biogenesis.',
      default: 'Exercise mimetic peptide that boosts mitochondrial function, endurance, and metabolic health — particularly valuable for athletes over 30.',
    },
  },
  ghkcu: {
    id: 'ghkcu', name: 'GHK-Cu', category: 'Anti-Aging & Repair', color: '#a78bfa',
    affiliateUrl: null,
    benefits: ['Skin rejuvenation', 'Collagen synthesis', 'Wound healing', 'Hair growth', 'Anti-inflammatory'],
    tags: ['anti-aging', 'recovery'],
    dosing: {
      male:   { under30: '100-200 mcg/day', '30-40': '200 mcg/day', '40-50': '200-300 mcg/day', over50: '200-300 mcg/day' },
      female: { under30: '100-200 mcg/day', '30-40': '200 mcg/day', '40-50': '200-300 mcg/day', over50: '200-300 mcg/day' },
    },
    frequency: '1x/day', route: 'SubQ or Topical',
    cycle: '4 weeks on / 2 weeks off', vialMg: 5, waterMl: 2, safetyRating: 5,
    research: 'Copper peptide naturally present in blood. Levels decline with age. Robust evidence for collagen synthesis, wound healing, and anti-inflammatory effects.',
    notes: 'Can also use topically for skin and hair benefits. SubQ for systemic effects. Very well tolerated.',
    whyFor: {
      'anti-aging': 'Stimulates collagen I and III synthesis, activates tissue remodeling genes, and reduces oxidative damage — comprehensive anti-aging at the cellular level.',
      recovery: 'Accelerates wound healing and reduces scarring. Useful for post-surgical or post-injury skin recovery.',
      default: 'Natural copper peptide that declines with age. Restores skin quality, collagen production, and tissue repair capacity.',
    },
  },
  epitalon: {
    id: 'epitalon', name: 'Epitalon', category: 'Longevity', color: '#818cf8',
    affiliateUrl: null,
    benefits: ['Telomerase activation', 'Circadian rhythm regulation', 'Anti-aging', 'DNA repair'],
    tags: ['anti-aging'],
    dosing: {
      male:   { under30: '5 mg/day', '30-40': '5 mg/day', '40-50': '5 mg/day', over50: '5-10 mg/day' },
      female: { under30: '5 mg/day', '30-40': '5 mg/day', '40-50': '5 mg/day', over50: '5-10 mg/day' },
    },
    frequency: '1x/day (intensive cycles)', route: 'SubQ',
    cycle: '10 days on / 4-6 months off', vialMg: 10, waterMl: 2, safetyRating: 4,
    research: 'Pineal peptide (epithalamin). Research by Prof. Khavinson shows telomerase activation and life extension in animal models. 10-day intensive cycles 2-3x per year.',
    notes: 'Short intensive cycles — NOT continuous use. 10 days on, then 4-6 months off. 2-3 cycles per year.',
    articleSlug: 'epitalon-telomere-anti-aging-peptide-guide',
    whyFor: {
      'anti-aging': 'Activates telomerase enzyme to maintain telomere length — the most direct peptide intervention for biological aging.',
      default: 'Longevity peptide that targets telomere maintenance. Used in short intensive cycles for maximum effect.',
    },
  },
  tesamorelin: {
    id: 'tesamorelin', name: 'Tesamorelin', category: 'GH / Fat Loss', color: '#f59e0b',
    affiliateUrl: null,
    benefits: ['Visceral fat reduction', 'GH stimulation', 'Cognitive support', 'Triglyceride reduction'],
    tags: ['body-comp', 'anti-aging', 'cognitive'],
    dosing: {
      male:   { under30: '1-2 mg/day', '30-40': '2 mg/day', '40-50': '2 mg/day', over50: '2 mg/day' },
      female: { under30: '1 mg/day', '30-40': '1-2 mg/day', '40-50': '2 mg/day', over50: '2 mg/day' },
    },
    frequency: '1x/day (morning, empty stomach)', route: 'SubQ (abdomen)',
    cycle: '12 weeks on / 4 weeks off', vialMg: 2, waterMl: 2, safetyRating: 4,
    research: 'FDA-approved (Egrifta) for HIV-associated lipodystrophy. One of the only peptides with FDA approval. Strong evidence for visceral fat reduction.',
    notes: 'Take on empty stomach in the morning. FDA-approved, which is rare for peptides. Primarily targets visceral (organ) fat.',
    articleSlug: 'tesamorelin-visceral-fat-peptide-guide',
    whyFor: {
      'body-comp': 'FDA-approved for visceral fat reduction. One of the few peptides with rigorous clinical trial data for body composition improvement.',
      'anti-aging': 'Reduces dangerous visceral fat and stimulates GH — addressing two major drivers of metabolic aging.',
      cognitive: 'Emerging research shows cognitive benefits through GH-mediated neuroprotection and improved cerebral blood flow.',
      default: 'FDA-approved peptide targeting visceral fat — the most clinically validated peptide for body composition.',
    },
  },
  semaglutide: {
    id: 'semaglutide', name: 'Semaglutide', category: 'Metabolic', color: '#22c55e',
    affiliateUrl: null,
    benefits: ['Appetite regulation', 'Weight management', 'Metabolic health', 'Cardiovascular benefits'],
    tags: ['body-comp'],
    dosing: {
      male:   { under30: '0.25-1 mg/week', '30-40': '0.25-1 mg/week', '40-50': '0.5-1.7 mg/week', over50: '0.5-2.4 mg/week' },
      female: { under30: '0.25-0.5 mg/week', '30-40': '0.25-1 mg/week', '40-50': '0.5-1.7 mg/week', over50: '0.5-1.7 mg/week' },
    },
    frequency: '1x/week', route: 'SubQ',
    cycle: 'Continuous (titrate up over 4-8 weeks)', vialMg: 5, waterMl: 2, safetyRating: 5,
    research: 'FDA-approved (Ozempic/Wegovy). GLP-1 receptor agonist. Extensive clinical data for weight management and cardiovascular risk reduction.',
    notes: 'Titrate slowly: start low, increase every 4 weeks. Same day each week. Some GI side effects initially.',
    whyFor: {
      'body-comp': 'FDA-approved GLP-1 agonist with the strongest clinical data for sustained weight loss. Reduces appetite centrally while improving metabolic markers.',
      default: 'Gold standard for medical weight management with extensive safety data.',
    },
  },
  kpv: {
    id: 'kpv', name: 'KPV', category: 'Anti-Inflammatory', color: '#06B6D4',
    affiliateUrl: null,
    benefits: ['Potent anti-inflammatory', 'Gut healing', 'Skin conditions', 'Immune modulation'],
    tags: ['gut', 'recovery', 'anti-aging'],
    dosing: {
      male:   { under30: '200-500 mcg/day', '30-40': '200-500 mcg/day', '40-50': '500 mcg/day', over50: '500 mcg/day' },
      female: { under30: '200-400 mcg/day', '30-40': '200-500 mcg/day', '40-50': '500 mcg/day', over50: '500 mcg/day' },
    },
    frequency: '1-2x daily (or oral for gut)', route: 'SubQ, Topical, or Oral capsule',
    cycle: '4-8 weeks on / 2-4 weeks off', vialMg: 5, waterMl: 2, safetyRating: 4,
    research: 'Alpha-MSH fragment with potent anti-inflammatory properties. Shows efficacy for IBD, colitis, and systemic inflammation in preclinical models.',
    notes: 'Oral capsules target gut directly. SubQ for systemic inflammation. Topical for skin conditions.',
    articleSlug: 'kpv-peptide-master-anti-inflammatory-guide',
    whyFor: {
      gut: 'Master anti-inflammatory peptide — directly reduces gut inflammation (NF-kB pathway). Oral capsules deliver directly to the GI tract.',
      recovery: 'Reduces systemic inflammation that can impede recovery, particularly useful for athletes with high training volumes.',
      default: 'Powerful anti-inflammatory peptide derived from alpha-MSH. Particularly effective for gut and skin inflammation.',
    },
  },
  dihexa: {
    id: 'dihexa', name: 'Dihexa', category: 'Cognitive', color: '#818cf8',
    affiliateUrl: null,
    benefits: ['Cognitive enhancement', 'Neuroprotection', 'Memory improvement', 'Synaptogenesis'],
    tags: ['cognitive'],
    dosing: {
      male:   { under30: '10-20 mg/day', '30-40': '10-30 mg/day', '40-50': '20-30 mg/day', over50: '20-30 mg/day' },
      female: { under30: '10-20 mg/day', '30-40': '10-20 mg/day', '40-50': '10-30 mg/day', over50: '20-30 mg/day' },
    },
    frequency: '1x/day (oral or SubQ)', route: 'Oral (sublingual) or SubQ',
    cycle: '4-6 weeks on / 4 weeks off', vialMg: 10, waterMl: 2, safetyRating: 3,
    research: 'HGF mimetic shown to be 10 million times more potent than BDNF at promoting synaptogenesis in vitro. Promising for cognitive repair.',
    notes: 'Potent nootropic. Start low and assess tolerance. Cycling is important. Research is promising but still limited.',
    articleSlug: 'dihexa-peptide-brain-repair-what-the-science-actually-says',
    whyFor: {
      cognitive: 'The most potent cognitive peptide available — 10 million times more effective than BDNF at forming new neural connections (synaptogenesis).',
      default: 'Powerful nootropic peptide for cognitive enhancement and neuroprotection.',
    },
  },
  semax: {
    id: 'semax', name: 'Semax', category: 'Cognitive', color: '#60a5fa',
    affiliateUrl: null,
    benefits: ['Focus & attention', 'Neuroprotection', 'BDNF upregulation', 'Stress resilience'],
    tags: ['cognitive', 'performance'],
    dosing: {
      male:   { under30: '200-600 mcg/day', '30-40': '300-600 mcg/day', '40-50': '300-900 mcg/day', over50: '600-900 mcg/day' },
      female: { under30: '200-400 mcg/day', '30-40': '200-600 mcg/day', '40-50': '300-600 mcg/day', over50: '300-600 mcg/day' },
    },
    frequency: '1-2x daily', route: 'Intranasal',
    cycle: '10-20 days on / 10 days off', vialMg: 3, waterMl: 1, safetyRating: 4,
    research: 'Russian-developed nootropic with decades of clinical use. Upregulates BDNF by 300%+. Approved in Russia for cognitive disorders.',
    notes: 'Intranasal delivery for fastest brain penetration. Well-tolerated with decades of clinical data. Can be used with Selank for synergy.',
    whyFor: {
      cognitive: 'Upregulates BDNF by 300%+ and enhances focus, memory, and stress resilience — decades of clinical validation in Russia.',
      performance: 'Improves mental clarity and focus during training and competition. Helps with stress management in high-pressure situations.',
      default: 'Clinically validated nootropic peptide that enhances focus, memory, and neural resilience.',
    },
  },
  pt141: {
    id: 'pt141', name: 'PT-141', category: 'Libido & Hormonal', color: '#f97316',
    affiliateUrl: null,
    benefits: ['Sexual function enhancement', 'Libido improvement', 'Works centrally (brain-mediated)'],
    tags: ['hormonal'],
    dosing: {
      male:   { under30: '1-2 mg as needed', '30-40': '2 mg as needed', '40-50': '2 mg as needed', over50: '2 mg as needed' },
      female: { under30: '0.5-1 mg as needed', '30-40': '0.5-1.5 mg as needed', '40-50': '1-1.5 mg as needed', over50: '1-2 mg as needed' },
    },
    frequency: 'As needed (max 2x/week)', route: 'SubQ',
    cycle: 'As needed — not continuous', vialMg: 10, waterMl: 2, safetyRating: 4,
    research: 'FDA-approved as Vyleesi for female sexual dysfunction. Acts on melanocortin receptors in the brain to enhance desire.',
    notes: 'Use 2-4 hours before activity. May cause nausea initially — start with lower dose. Do not exceed 2x/week.',
    whyFor: {
      hormonal: 'FDA-approved approach to sexual function enhancement. Works through the brain (melanocortin receptors) rather than locally.',
      default: 'Brain-mediated sexual function enhancer — the only FDA-approved peptide for this purpose.',
    },
  },
  aod9604: {
    id: 'aod9604', name: 'AOD-9604', category: 'Fat Loss', color: '#f59e0b',
    affiliateUrl: null,
    benefits: ['Targeted fat loss', 'Does not affect IGF-1', 'Cartilage repair', 'Safe metabolic profile'],
    tags: ['body-comp'],
    dosing: {
      male:   { under30: '300-500 mcg/day', '30-40': '300-500 mcg/day', '40-50': '500 mcg/day', over50: '500 mcg/day' },
      female: { under30: '250-300 mcg/day', '30-40': '300 mcg/day', '40-50': '300-500 mcg/day', over50: '300-500 mcg/day' },
    },
    frequency: '1x/day (morning, fasted)', route: 'SubQ (abdomen)',
    cycle: '12 weeks on / 4 weeks off', vialMg: 5, waterMl: 2, safetyRating: 5,
    research: 'Fragment of human growth hormone (HGH 176-191). Stimulates lipolysis without the growth-promoting effects of full HGH.',
    notes: 'Take fasted in the morning for best results. Does NOT raise IGF-1 or blood sugar. Very safe metabolic profile.',
    articleSlug: 'aod-9604-fat-burning-peptide-guide',
    whyFor: {
      'body-comp': 'GH fragment that targets fat cells directly without the side effects of full GH. Safe, effective, and does not affect blood sugar or IGF-1.',
      default: 'Targeted fat loss peptide derived from GH — promotes lipolysis without growth-promoting side effects.',
    },
  },
  retatrutide: {
    id: 'retatrutide', name: 'Retatrutide', category: 'Fat Loss / Body Comp', color: '#f59e0b',
    affiliateUrl: null,
    benefits: ['Triple-agonist weight management (GLP-1/GIP/Glucagon)', 'Significant body fat reduction', 'Appetite suppression', 'Improved metabolic markers', 'Potential muscle preservation'],
    tags: ['body-comp', 'performance'],
    dosing: {
      male:   { under30: '1-4 mg/week (titrate)', '30-40': '2-4 mg/week (titrate)', '40-50': '2-4 mg/week (titrate)', over50: '1-4 mg/week (titrate)' },
      female: { under30: '0.5-2 mg/week (titrate)', '30-40': '1-4 mg/week (titrate)', '40-50': '1-4 mg/week (titrate)', over50: '1-2 mg/week (titrate)' },
    },
    frequency: '1x/week (SubQ)', route: 'SubQ (abdomen or thigh)',
    cycle: '24-48 weeks (clinical trial duration), titrate dose slowly', vialMg: 4, waterMl: 2, safetyRating: 3,
    research: 'Triple agonist targeting GLP-1, GIP, and glucagon receptors simultaneously. Phase 2 clinical trials (2023) showed up to 24.2% body weight reduction at 48 weeks — the most potent weight loss observed in any incretin-based therapy to date. Glucagon receptor activation drives increased energy expenditure and lipid oxidation.',
    notes: 'Start at the lowest dose and titrate slowly over weeks to minimize GI side effects. Nausea, vomiting, and diarrhea are common during dose escalation. Monitor blood glucose and thyroid function. Not yet FDA-approved — research compound.',
    articleSlug: null,
    whyFor: {
      'body-comp': 'The most potent incretin-based compound studied to date. Triple receptor agonism (GLP-1/GIP/Glucagon) produces superior fat loss vs GLP-1 alone, with glucagon-driven energy expenditure helping preserve lean mass.',
      performance: 'Athletes exploring Retatrutide for body recomposition benefit from its glucagon-receptor activation, which increases fat oxidation during exercise while maintaining metabolic rate.',
      default: 'Next-generation triple-agonist peptide for body composition — targets GLP-1, GIP, and glucagon receptors for maximum fat loss with potential muscle-sparing effects.',
    },
  },
};

// ═══════════════════════════════════════════════
// USE-CASE RECOMMENDATION ENGINE
// ═══════════════════════════════════════════════

const ACTIVITIES = [
  { id: 'hybrid', label: 'HYROX / Hybrid', desc: 'Mixed endurance + functional' },
  { id: 'crossfit', label: 'CrossFit', desc: 'High intensity functional' },
  { id: 'running', label: 'Running / Endurance', desc: 'Distance or trail' },
  { id: 'strength', label: 'Strength / Powerlifting', desc: 'Heavy compound lifts' },
  { id: 'general', label: 'General Fitness', desc: 'Overall health focus' },
  { id: 'biohacking', label: 'Biohacking / Longevity', desc: 'Optimization-focused' },
];

const GOALS = [
  { id: 'recovery', label: 'Recovery & Injury Prevention', icon: HeartPulse, color: '#E63946' },
  { id: 'performance', label: 'Performance & Endurance', icon: Zap, color: '#00F0FF' },
  { id: 'body-comp', label: 'Body Composition', icon: Flame, color: '#f59e0b' },
  { id: 'sleep', label: 'Sleep & Deep Recovery', icon: Moon, color: '#a78bfa' },
  { id: 'anti-aging', label: 'Anti-Aging & Longevity', icon: Sparkles, color: '#E63946' },
  { id: 'cognitive', label: 'Cognitive Enhancement', icon: Brain, color: '#818cf8' },
  { id: 'gut', label: 'Gut Health', icon: HeartPulse, color: '#22c55e' },
  { id: 'joint', label: 'Joint & Tendon Health', icon: Shield, color: '#06B6D4' },
];

const HEALTH_FLAGS = [
  { id: 'injury', label: 'Currently dealing with an injury' },
  { id: 'gut-issues', label: 'Gut issues (IBS, bloating, leaky gut)' },
  { id: 'poor-sleep', label: 'Poor sleep quality' },
  { id: 'brain-fog', label: 'Brain fog or cognitive decline' },
  { id: 'chronic-inflammation', label: 'Chronic inflammation / joint pain' },
  { id: 'low-energy', label: 'Low energy / fatigue' },
];

function generateRecommendation(profile) {
  const { sex, ageRange, activity, goals, healthFlags } = profile;
  const core = [];
  const optional = [];
  const allTags = new Set([...goals, ...healthFlags.map(f => {
    if (f === 'injury') return 'injury';
    if (f === 'gut-issues') return 'gut';
    if (f === 'poor-sleep') return 'sleep';
    if (f === 'brain-fog') return 'cognitive';
    if (f === 'chronic-inflammation') return 'joint';
    if (f === 'low-energy') return 'performance';
    return f;
  })]);

  // Always include BPC-157 if recovery, injury, gut, or joint is a goal
  if (['recovery', 'injury', 'gut', 'joint'].some(t => allTags.has(t))) {
    core.push('bpc157');
  }

  // TB-500 for serious injury or joint focus
  if (allTags.has('injury') || (allTags.has('joint') && allTags.has('recovery'))) {
    core.push('tb500');
  } else if (allTags.has('joint')) {
    optional.push('tb500');
  }

  // CJC/Ipa for sleep, recovery, body-comp, anti-aging, or performance
  if (['sleep', 'recovery', 'body-comp', 'anti-aging', 'performance'].some(t => allTags.has(t))) {
    core.push('cjc-ipa');
  }

  // MOTS-C for performance, endurance activities, body-comp for 30+
  if (allTags.has('performance') || ['hybrid', 'crossfit', 'running'].includes(activity)) {
    core.push('motsc');
  } else if (allTags.has('body-comp') && ageRange !== 'under30') {
    optional.push('motsc');
  }

  // Body comp goals
  if (allTags.has('body-comp')) {
    if (!core.includes('cjc-ipa')) core.push('cjc-ipa');
    if (ageRange === '40-50' || ageRange === 'over50') {
      core.push('tesamorelin');
    } else {
      optional.push('aod9604');
    }
    optional.push('retatrutide');
  }

  // Anti-aging
  if (allTags.has('anti-aging')) {
    core.push('ghkcu');
    optional.push('epitalon');
  }

  // Gut health
  if (allTags.has('gut')) {
    if (!core.includes('bpc157')) core.push('bpc157');
    optional.push('kpv');
  }

  // Cognitive
  if (allTags.has('cognitive')) {
    core.push('semax');
    optional.push('dihexa');
  }

  // Activity-specific adjustments
  if (activity === 'biohacking' && !core.includes('ghkcu')) {
    core.push('ghkcu');
    if (!optional.includes('epitalon')) optional.push('epitalon');
  }

  // Deduplicate
  const coreUnique = [...new Set(core)].filter(id => PEPTIDE_DB[id]);
  const optUnique = [...new Set(optional)].filter(id => PEPTIDE_DB[id] && !coreUnique.includes(id));

  // Build result with personalized dosing
  const getDose = (p) => {
    const sexDosing = p.dosing[sex] || p.dosing.male;
    return sexDosing[ageRange] || sexDosing['30-40'];
  };

  const getWhyFor = (p) => {
    for (const goal of goals) {
      if (p.whyFor[goal]) return p.whyFor[goal];
    }
    for (const tag of allTags) {
      if (p.whyFor[tag]) return p.whyFor[tag];
    }
    return p.whyFor.default || p.research;
  };

  const stackName = buildStackName(profile);

  return {
    stackName,
    profile: { sex, ageRange, activity, goals, healthFlags },
    core: coreUnique.map(id => ({ ...PEPTIDE_DB[id], personalDose: getDose(PEPTIDE_DB[id]), personalWhy: getWhyFor(PEPTIDE_DB[id]) })),
    optional: optUnique.map(id => ({ ...PEPTIDE_DB[id], personalDose: getDose(PEPTIDE_DB[id]), personalWhy: getWhyFor(PEPTIDE_DB[id]) })),
    synergy: buildSynergyNotes(coreUnique),
  };
}

function buildStackName(profile) {
  const ageLabel = { under30: '20s', '30-40': '30s', '40-50': '40s', over50: '50+' }[profile.ageRange] || '';
  const sexLabel = profile.sex === 'female' ? 'Female' : 'Male';
  const actLabel = ACTIVITIES.find(a => a.id === profile.activity)?.label || 'Athlete';
  return `${sexLabel} ${actLabel} in ${ageLabel}`;
}

function buildSynergyNotes(peptideIds) {
  const notes = [];
  if (peptideIds.includes('bpc157') && peptideIds.includes('tb500'))
    notes.push({ title: 'The Wolverine Stack', text: 'BPC-157 + TB-500 is the gold-standard healing combo. BPC-157 targets localized repair while TB-500 provides systemic reach. Together they dramatically accelerate recovery.' });
  if (peptideIds.includes('cjc-ipa') && peptideIds.includes('motsc'))
    notes.push({ title: 'GH + Metabolic Synergy', text: 'CJC/Ipa enhances GH-driven recovery overnight while MOTS-C boosts mitochondrial function during training. Together, they optimize both the anabolic and metabolic pathways.' });
  if (peptideIds.includes('bpc157') && peptideIds.includes('cjc-ipa'))
    notes.push({ title: 'Recovery Amplifier', text: 'BPC-157 provides direct tissue repair while CJC/Ipa amplifies the body\'s natural GH-driven healing cascade. The combination accelerates return-to-training timelines.' });
  if (peptideIds.includes('ghkcu') && peptideIds.includes('epitalon'))
    notes.push({ title: 'Anti-Aging Synergy', text: 'GHK-Cu restores collagen and tissue quality while Epitalon targets telomere maintenance. Together, they address aging from both the structural and genetic level.' });
  if (peptideIds.includes('tesamorelin') && peptideIds.includes('cjc-ipa'))
    notes.push({ title: 'Dual GH Pathway', text: 'Tesamorelin targets visceral fat specifically while CJC/Ipa supports overall GH optimization. Separating doses (morning/night) maximizes both pathways.' });
  return notes;
}

// ═══════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PeptideStackBuilderPage() {
  const [mode, setMode] = useState('personalized'); // 'personalized' | 'manual'
  const [wizardStep, setWizardStep] = useState(1);
  const [sex, setSex] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [activity, setActivity] = useState('');
  const [goals, setGoals] = useState([]);
  const [healthFlags, setHealthFlags] = useState([]);
  const [recommendation, setRecommendation] = useState(null);
  const [expandedPeptide, setExpandedPeptide] = useState(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [abVariant, setAbVariant] = useState(null);
  const [abTestId, setAbTestId] = useState(null);
  const { saveResult } = useResults();

  // Fetch A/B test variant on mount
  useState(() => {
    const cached = sessionStorage.getItem('stack_ab');
    if (cached) {
      try { const p = JSON.parse(cached); setAbVariant(p.variant); setAbTestId(p.test_id); return; } catch (e) { console.error('Failed to parse cached stack A/B variant', e); }
    }
    fetch(`${API_URL}/api/stack-ab/variant`)
      .then(r => r.json())
      .then(d => {
        if (d.variant) { setAbVariant(d.variant); setAbTestId(d.test_id); sessionStorage.setItem('stack_ab', JSON.stringify(d)); }
      }).catch(() => {});
  }, []);

  // eslint-disable-next-line no-nested-ternary -- readable tier picker
  const toggleGoal = (id) => setGoals(p => p.includes(id) ? p.filter(x => x !== id) : p.length < 3 ? [...p, id] : p);
  const toggleFlag = (id) => setHealthFlags(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const canAdvance = useMemo(() => {
    if (wizardStep === 1) return sex && ageRange;
    if (wizardStep === 2) return activity;
    if (wizardStep === 3) return goals.length > 0;
    return true;
  }, [wizardStep, sex, ageRange, activity, goals]);

  const handleGenerate = useCallback(() => {
    const maxCore = abVariant?.max_core || 99;
    const includeOptional = abVariant?.include_optional !== false;
    const result = generateRecommendation({ sex, ageRange, activity, goals, healthFlags });
    // Apply A/B variant constraints
    if (result.core.length > maxCore) result.core = result.core.slice(0, maxCore);
    if (!includeOptional) result.optional = [];
    setRecommendation(result);
    setWizardStep(5);
    // Track impression
    if (abTestId && abVariant) {
      fetch(`${API_URL}/api/stack-ab/track`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_id: abTestId, variant_id: abVariant.variant_id, event: 'impression', profile: { sex, ageRange, activity, goals } }),
      }).catch(() => {});
    }
  }, [sex, ageRange, activity, goals, healthFlags, abVariant, abTestId]);

  const handleSave = useCallback(() => {
    if (!recommendation) return;
    saveResult('peptideStack', {
      value: `${recommendation.core.length + recommendation.optional.length} peptides`,
      label: recommendation.stackName,
      core: recommendation.core.map(p => p.name).join(', '),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, [recommendation, saveResult]);

  const handleShare = useCallback(async () => {
    if (!recommendation) return;
    const text = `My Personalized Peptide Stack — ${recommendation.stackName}\n\nCore Stack:\n${recommendation.core.map(p => `• ${p.name}: ${p.personalDose} — ${p.benefits[0]}`).join('\n')}${recommendation.optional.length ? `\n\nOptional Additions:\n${recommendation.optional.map(p => `• ${p.name}: ${p.personalDose}`).join('\n')}` : ''}\n\nBuild yours: ninjathlete.com/tools/peptide-stack`;
    try {
      if (navigator.share) await navigator.share({ title: 'My Peptide Stack', text });
      else { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    } catch { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  }, [recommendation]);

  return (
    <main className="pt-20 min-h-screen" data-testid="peptide-stack-page">
      <section className="container-ninja section-spacing">
        <div className="max-w-4xl mx-auto">
          <Link to="/peptides" className="text-zinc-500 text-xs font-heading uppercase tracking-wider hover:text-white transition-colors inline-flex items-center gap-1 mb-6" data-testid="back-to-hub">
            <ArrowLeft className="w-3 h-3" /> Peptides Hub
          </Link>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center bg-[#E63946]/10 border border-[#E63946]/30">
              <FlaskConical className="w-5 h-5 text-[#E63946]" />
            </div>
            <h1 className="text-3xl sm:text-4xl text-white font-heading" data-testid="page-title" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Customize Your Peptide Stack
            </h1>
          </div>
          <p className="text-zinc-400 text-sm mb-6">
            Answer a few questions about your profile and goals. We'll build a research-backed, personalized peptide protocol tailored to your age, sex, activity level, and objectives.
          </p>

          {/* SEO/AI-crawler discoverability: render the APR affiliate URL in the
              initial DOM so bots see the .net /ref/16/ link without needing to
              walk the interactive quiz. */}
          <AprTrustBlock variant="full" cta="Shop APR · 25% off" />

          {/* Mode toggle */}
          <div className="flex gap-2 mb-8">
            <button onClick={() => { setMode('personalized'); setRecommendation(null); setWizardStep(1); }}
              className={`px-4 py-2 text-xs uppercase tracking-wider font-heading transition-colors ${mode === 'personalized' ? 'bg-[#E63946] text-white' : 'border border-zinc-700 text-zinc-500 hover:text-white'}`}
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="mode-personalized">
              Personalized
            </button>
            <button onClick={() => setMode('manual')}
              className={`px-4 py-2 text-xs uppercase tracking-wider font-heading transition-colors ${mode === 'manual' ? 'bg-[#E63946] text-white' : 'border border-zinc-700 text-zinc-500 hover:text-white'}`}
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="mode-manual">
              Manual Builder
            </button>
          </div>

          {/* ═══════════════════════════════════════════
              PERSONALIZED MODE — WIZARD
             ═══════════════════════════════════════════ */}
          {mode === 'personalized' && !recommendation && (
            <div data-testid="wizard-section">
              {/* Progress */}
              <div className="flex items-center gap-1 mb-8">
                {[1,2,3,4].map(s => (
                  <div key={s} className={`flex-1 h-1 transition-all duration-300 ${wizardStep >= s ? 'bg-[#E63946]' : 'bg-zinc-800'}`} />
                ))}
              </div>

              {/* STEP 1: Profile */}
              {wizardStep === 1 && (
                <div className="space-y-6" data-testid="wizard-step-1">
                  <h2 className="text-white text-xl font-heading" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Your Profile</h2>

                  <div>
                    <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Biological Sex</label>
                    <div className="flex gap-3">
                      {[{ id: 'male', label: 'Male' }, { id: 'female', label: 'Female' }].map(s => (
                        <button key={s.id} onClick={() => setSex(s.id)}
                          className={`flex-1 py-3 border text-sm font-heading uppercase tracking-wider transition-all ${sex === s.id ? 'border-[#E63946] bg-[#E63946]/10 text-white' : 'border-zinc-700 text-zinc-500 hover:text-white'}`}
                          style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                          data-testid={`sex-${s.id}`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Age Range</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { id: 'under30', label: 'Under 30' },
                        { id: '30-40', label: '30 – 40' },
                        { id: '40-50', label: '40 – 50' },
                        { id: 'over50', label: '50+' },
                      ].map(a => (
                        <button key={a.id} onClick={() => setAgeRange(a.id)}
                          className={`py-3 border text-sm font-heading uppercase tracking-wider transition-all ${ageRange === a.id ? 'border-[#E63946] bg-[#E63946]/10 text-white' : 'border-zinc-700 text-zinc-500 hover:text-white'}`}
                          style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                          data-testid={`age-${a.id}`}>
                          {a.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: Activity */}
              {wizardStep === 2 && (
                <div className="space-y-4" data-testid="wizard-step-2">
                  <h2 className="text-white text-xl font-heading" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Primary Activity</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {ACTIVITIES.map(a => (
                      <button key={a.id} onClick={() => setActivity(a.id)}
                        className={`p-4 text-left border transition-all ${activity === a.id ? 'border-[#E63946] bg-[#E63946]/5' : 'border-zinc-800 hover:border-zinc-700'}`}
                        data-testid={`activity-${a.id}`}>
                        <p className={`text-sm font-heading uppercase tracking-wider ${activity === a.id ? 'text-white' : 'text-zinc-300'}`} style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{a.label}</p>
                        <p className="text-zinc-500 text-xs mt-0.5">{a.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 3: Goals */}
              {wizardStep === 3 && (
                <div className="space-y-4" data-testid="wizard-step-3">
                  <h2 className="text-white text-xl font-heading" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Your Top Goals <span className="text-zinc-500 text-xs">(Pick up to 3)</span></h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {GOALS.map(g => {
                      const Icon = g.icon;
                      const isSelected = goals.includes(g.id);
                      return (
                        <button key={g.id} onClick={() => toggleGoal(g.id)}
                          className={`p-4 text-left border transition-all flex items-center gap-3 ${isSelected ? 'border-opacity-60 bg-opacity-5' : 'border-zinc-800 hover:border-zinc-700'}`}
                          style={{ borderColor: isSelected ? g.color : undefined, backgroundColor: isSelected ? `${g.color}08` : undefined }}
                          data-testid={`goal-${g.id}`}>
                          <Icon className="w-5 h-5 flex-shrink-0" style={{ color: isSelected ? g.color : '#71717a' }} />
                          <span className={`text-sm ${isSelected ? 'text-white' : 'text-zinc-400'}`}>{g.label}</span>
                          {isSelected && <Check className="w-4 h-4 ml-auto" style={{ color: g.color }} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* STEP 4: Health Considerations */}
              {wizardStep === 4 && (
                <div className="space-y-4" data-testid="wizard-step-4">
                  <h2 className="text-white text-xl font-heading" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Health Considerations <span className="text-zinc-500 text-xs">(Optional)</span></h2>
                  <p className="text-zinc-500 text-xs">Select any that apply — this helps fine-tune your stack.</p>
                  <div className="space-y-2">
                    {HEALTH_FLAGS.map(f => (
                      <button key={f.id} onClick={() => toggleFlag(f.id)}
                        className={`w-full p-3 text-left border text-sm transition-all flex items-center gap-3 ${healthFlags.includes(f.id) ? 'border-[#E63946]/40 bg-[#E63946]/5 text-white' : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'}`}
                        data-testid={`flag-${f.id}`}>
                        <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${healthFlags.includes(f.id) ? 'bg-[#E63946] border-[#E63946]' : 'border-zinc-600'}`}>
                          {healthFlags.includes(f.id) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-3 mt-8">
                {wizardStep > 1 && (
                  <button onClick={() => setWizardStep(s => s - 1)}
                    className="px-6 py-3 border border-zinc-700 text-zinc-400 font-heading text-sm uppercase tracking-wider hover:text-white transition-colors"
                    style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                    data-testid="wizard-back">
                    <ArrowLeft className="w-4 h-4 inline mr-1" /> Back
                  </button>
                )}
                {wizardStep < 4 ? (
                  <button onClick={() => setWizardStep(s => s + 1)} disabled={!canAdvance}
                    className="flex-1 py-3 bg-[#E63946] text-white font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-30"
                    style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                    data-testid="wizard-next">
                    Next <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button onClick={handleGenerate}
                    className="flex-1 py-3 bg-[#E63946] text-white font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                    data-testid="generate-stack">
                    Generate My Stack <FlaskConical className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════
              PERSONALIZED MODE — RESULTS
             ═══════════════════════════════════════════ */}
          {mode === 'personalized' && recommendation && (
            <div data-testid="recommendation-section">
              {/* Stack Title */}
              <div className="bg-gradient-to-r from-[#E63946]/10 to-transparent border border-[#E63946]/20 p-6 mb-8">
                <p className="text-[#E63946] text-[10px] uppercase tracking-widest mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Your Personalized Protocol</p>
                <h2 className="text-white text-2xl md:text-3xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="stack-title">
                  {recommendation.stackName}
                </h2>
                <p className="text-zinc-400 text-sm mt-2">
                  {recommendation.core.length} core peptide{recommendation.core.length !== 1 ? 's' : ''}{recommendation.optional.length > 0 ? ` + ${recommendation.optional.length} optional` : ''} — tailored to your profile and goals.
                </p>
              </div>

              {/* Core Stack */}
              <div className="mb-8">
                <h3 className="text-white text-lg font-heading mb-4 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  <FlaskConical className="w-5 h-5 text-[#E63946]" /> Your Core Stack
                </h3>
                <div className="space-y-4">
                  {recommendation.core.map(p => (
                    <PeptideRecommendationCard key={p.id} peptide={p} stackProfile={recommendation.profile} abTestId={abTestId} abVariantId={abVariant?.variant_id} expanded={expandedPeptide === p.id} onToggle={() => setExpandedPeptide(expandedPeptide === p.id ? null : p.id)} />
                  ))}
                </div>
              </div>

              {/* Optional */}
              {recommendation.optional.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-white text-lg font-heading mb-4 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    <Target className="w-5 h-5 text-zinc-500" /> Consider Adding
                  </h3>
                  <div className="space-y-4">
                    {recommendation.optional.map(p => (
                      <PeptideRecommendationCard key={p.id} peptide={p} stackProfile={recommendation.profile} abTestId={abTestId} abVariantId={abVariant?.variant_id} isOptional expanded={expandedPeptide === p.id} onToggle={() => setExpandedPeptide(expandedPeptide === p.id ? null : p.id)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Synergy Notes */}
              {recommendation.synergy.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-white text-lg font-heading mb-4 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    <Zap className="w-5 h-5 text-[#00F0FF]" /> Stack Synergies
                  </h3>
                  <div className="space-y-3">
                    {recommendation.synergy.map((s, i) => (
                      <div key={s.title} className="bg-zinc-900/60 border border-zinc-800 p-4" data-testid={`synergy-${i}`}>
                        <p className="text-[#00F0FF] text-xs font-heading uppercase tracking-wider mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{s.title}</p>
                        <p className="text-zinc-400 text-sm">{s.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3 mb-6">

              {/* Affiliate Promo Banner */}
              {recommendation.core.concat(recommendation.optional).some(p => p.affiliateUrl) && (
                <div className="w-full bg-gradient-to-r from-[#E63946]/10 to-[#E63946]/5 border border-[#E63946]/20 p-4 flex items-center justify-between flex-wrap gap-3 mb-6" data-testid="affiliate-promo-banner">
                  <div>
                    <p className="text-white text-sm font-heading" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      Source Your Peptides
                    </p>
                    <p className="text-zinc-400 text-xs">
                      Use code <strong className="text-[#FFD100]">NINJA</strong> for 25% off at American Peptide Research — our trusted supplier for research-grade peptides.
                    </p>
                  </div>
                  <a href="https://www.americanpeptideresearch.net/ref/16/" target="_blank" rel="sponsored noopener"
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#E63946] text-white text-xs font-heading uppercase tracking-wider hover:opacity-90 transition-opacity flex-shrink-0"
                    style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                    data-testid="promo-shop-btn">
                    <ShoppingCart className="w-4 h-4" /> Shop Now <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
                <button onClick={handleSave}
                  className="px-6 py-3 bg-[#E63946] text-white font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity flex items-center gap-2"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  data-testid="save-stack-btn">
                  {saved ? <><Check className="w-4 h-4" /> Saved</> : <><Save className="w-4 h-4" /> Save Stack</>}
                </button>
                <button onClick={handleShare}
                  className="px-6 py-3 border border-[#00F0FF]/40 text-[#00F0FF] font-heading text-sm uppercase tracking-wider hover:bg-[#00F0FF]/10 transition-colors flex items-center gap-2"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  data-testid="share-stack-btn">
                  {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Share2 className="w-4 h-4" /> Share</>}
                </button>
                <button onClick={() => { setRecommendation(null); setWizardStep(1); }}
                  className="px-6 py-3 border border-zinc-700 text-zinc-400 font-heading text-sm uppercase tracking-wider hover:text-white transition-colors"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  data-testid="restart-btn">
                  Start Over
                </button>
              </div>

              {/* Disclaimer */}
              <div className="flex items-start gap-3 bg-[#f59e0b]/5 border border-[#f59e0b]/20 p-4 mb-6">
                <AlertTriangle className="w-5 h-5 text-[#f59e0b] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-zinc-300 text-xs"><strong className="text-[#f59e0b]">Medical Disclaimer:</strong> This tool is for informational purposes only. Peptides are research compounds. Always consult a licensed healthcare provider before starting any peptide protocol. Individual responses vary — what works for one person may not be appropriate for another.</p>
                </div>
              </div>

              {/* Related Tools */}
              <div className="flex flex-wrap gap-3">
                <Link to="/tools/reconstitution-guide" className="text-[#E63946] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  <Beaker className="w-3 h-3" /> Reconstitution Guide <ArrowRight className="w-3 h-3" />
                </Link>
                <Link to="/tools/peptide-dosing" className="text-[#E63946] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  <Syringe className="w-3 h-3" /> Dosing Calculator <ArrowRight className="w-3 h-3" />
                </Link>
                <Link to="/tools/peptide-cycle" className="text-[#E63946] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  <Repeat className="w-3 h-3" /> Cycle Tracker <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════
              MANUAL MODE (original builder)
             ═══════════════════════════════════════════ */}
          {mode === 'manual' && <ManualBuilder />}
        </div>
      </section>
    </main>
  );
}

// ═══════════════════════════════════════════════
// PEPTIDE RECOMMENDATION CARD
// ═══════════════════════════════════════════════

function PeptideRecommendationCard({ peptide, stackProfile, abTestId, abVariantId, isOptional, expanded, onToggle }) {
  const p = peptide;
  const trackAffiliate = () => {
    fetch(`${API_URL}/api/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'affiliate_click',
        url: p.affiliateUrl,
        product_name: p.name,
        source_page: '/tools/peptide-stack',
        stack_profile: stackProfile || {},
        referrer: window.location.pathname,
      }),
    }).catch(() => {});
    // Track A/B click
    if (abTestId && abVariantId) {
      fetch(`${API_URL}/api/stack-ab/track`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_id: abTestId, variant_id: abVariantId, event: 'click', peptide: p.name }),
      }).catch(() => {});
    }
  };
  return (
    <div className={`bg-zinc-900/60 border transition-all ${isOptional ? 'border-zinc-800' : 'border-zinc-700'}`}
      style={{ borderLeftColor: p.color, borderLeftWidth: '3px' }}
      data-testid={`peptide-card-${p.id}`}>
      <button onClick={onToggle} className="w-full p-4 text-left">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="text-white text-base font-heading" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{p.name}</h4>
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ color: p.color, borderColor: `${p.color}30` }}>{p.category}</span>
              <SafetyBadge rating={p.safetyRating} />
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">{p.personalWhy}</p>
            <div className="flex items-center gap-4 mt-3 text-xs flex-wrap">
              <span className="text-white font-semibold">{p.personalDose}</span>
              <span className="text-zinc-500">{p.frequency}</span>
              <span className="text-zinc-600">{p.cycle}</span>
              {p.affiliateUrl && (
                <a href={p.affiliateUrl} target="_blank" rel="sponsored noopener"
                  onClick={(e) => { e.stopPropagation(); trackAffiliate(); }}
                  className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 bg-[#E63946]/10 border border-[#E63946]/30 text-[#E63946] text-[10px] uppercase tracking-wider hover:bg-[#E63946]/20 transition-colors"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  data-testid={`buy-btn-${p.id}`}>
                  <ShoppingCart className="w-3 h-3" /> Buy
                </a>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 ml-3">
            {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-zinc-800 pt-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <DetailItem label="Route" value={p.route} />
            <DetailItem label="Cycle" value={p.cycle} />
            <DetailItem label="Vial Size" value={`${p.vialMg}mg`} />
            <DetailItem label="BAC Water" value={`${p.waterMl}mL`} />
          </div>

          <div>
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Benefits</p>
            <div className="flex flex-wrap gap-1.5">
              {p.benefits.map(b => (
                <span key={b} className="text-[10px] px-2 py-0.5 border border-zinc-700 text-zinc-400">{b}</span>
              ))}
            </div>
          </div>

          <div>
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Research</p>
            <p className="text-zinc-400 text-xs leading-relaxed">{p.research}</p>
          </div>

          <div className="flex items-start gap-2 bg-zinc-800/50 p-3">
            <Info className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0 mt-0.5" />
            <p className="text-zinc-500 text-xs leading-relaxed">{p.notes}</p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {p.articleSlug && (
              <Link to={`/blogs/article/${p.articleSlug}`} className="inline-flex items-center gap-1 text-[#E63946] text-xs font-heading uppercase tracking-wider hover:text-white transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid={`article-link-${p.id}`}>
                <BookOpen className="w-3 h-3" /> Read Full Guide <ExternalLink className="w-3 h-3" />
              </Link>
            )}
            {p.affiliateUrl && (
              <a href={p.affiliateUrl} target="_blank" rel="sponsored noopener"
                onClick={trackAffiliate}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#E63946] text-white text-xs font-heading uppercase tracking-wider hover:opacity-90 transition-opacity"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                data-testid={`buy-expanded-${p.id}`}>
                <ShoppingCart className="w-3.5 h-3.5" /> Buy {p.name} <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="bg-zinc-800/50 p-2">
      <p className="text-zinc-600 text-[9px] uppercase tracking-wider">{label}</p>
      <p className="text-zinc-300 text-xs">{value}</p>
    </div>
  );
}

function SafetyBadge({ rating }) {
  const labels = { 5: 'Very Safe', 4: 'Safe', 3: 'Moderate', 2: 'Use Caution', 1: 'Research Only' };
  const colors = { 5: '#22c55e', 4: '#22c55e', 3: '#f59e0b', 2: '#f97316', 1: '#E63946' };
  return (
    <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ color: colors[rating], borderColor: `${colors[rating]}30` }} data-testid={`safety-${rating}`}>
      {labels[rating]}
    </span>
  );
}

// ═══════════════════════════════════════════════
// MANUAL BUILDER (preserved from original)
// ═══════════════════════════════════════════════

const MANUAL_PEPTIDES = Object.values(PEPTIDE_DB).map(p => ({
  id: p.id, name: p.name, category: p.category, color: p.color,
  defaultDose: parseInt(p.dosing.male['30-40']) || 250,
  unit: p.dosing.male['30-40']?.includes('mg') ? 'mcg' : 'mcg',
  frequency: p.frequency, route: p.route,
  vialMg: p.vialMg, waterMl: p.waterMl,
  cycleOn: parseInt(p.cycle) || 28, cycleOff: 14,
  purpose: p.benefits.join(', '), notes: p.notes,
}));

const PRESET_STACKS = [
  { id: 'healing', name: 'Injury Healing', peptides: ['bpc157', 'tb500'], desc: 'BPC-157 + TB-500 for maximum tissue repair.' },
  { id: 'body-comp', name: 'Body Comp', peptides: ['cjc-ipa', 'tesamorelin'], desc: 'GH optimization for fat loss and lean mass.' },
  { id: 'weight-loss', name: 'Weight Loss', peptides: ['retatrutide', 'aod9604'], desc: 'Retatrutide triple-agonist + AOD-9604 for maximum fat loss.' },
  { id: 'longevity', name: 'Longevity', peptides: ['ghkcu', 'epitalon', 'motsc'], desc: 'Comprehensive anti-aging protocol.' },
  { id: 'athlete', name: 'Athlete Recovery', peptides: ['bpc157', 'tb500', 'cjc-ipa'], desc: 'Maximum recovery stack for competitive athletes.' },
  { id: 'cognitive', name: 'Brain Stack', peptides: ['semax', 'dihexa'], desc: 'Focus, memory, and neuroprotection.' },
];

function ManualBuilder() {
  const [selected, setSelected] = useState([]);
  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const applyStack = (ids) => setSelected(ids);

  const selectedPeptides = MANUAL_PEPTIDES.filter(p => selected.includes(p.id));

  return (
    <div data-testid="manual-builder">
      {/* Preset Stacks */}
      <div className="mb-6">
        <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Quick Stacks</p>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {PRESET_STACKS.map(s => (
            <button key={s.id} onClick={() => applyStack(s.peptides)}
              className="bg-zinc-900/80 border border-zinc-800 hover:border-[#E63946]/40 p-3 text-left transition-all"
              data-testid={`preset-${s.id}`}>
              <p className="text-white text-xs font-heading" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{s.name}</p>
              <p className="text-zinc-600 text-[10px] mt-0.5">{s.peptides.length} peptides</p>
            </button>
          ))}
        </div>
      </div>

      {/* Peptide Grid */}
      <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>All Peptides</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {MANUAL_PEPTIDES.map(p => {
          const isSelected = selected.includes(p.id);
          return (
            <div key={p.id} onClick={() => toggle(p.id)}
              className={`bg-zinc-900/80 border p-4 cursor-pointer transition-all ${isSelected ? 'border-opacity-60' : 'border-zinc-800 hover:border-zinc-700'}`}
              style={{ borderColor: isSelected ? p.color : undefined }}
              data-testid={`manual-peptide-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white text-sm font-heading" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{p.name}</h3>
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ color: p.color, borderColor: `${p.color}30` }}>{p.category}</span>
                  </div>
                  <p className="text-zinc-400 text-xs">{p.purpose}</p>
                  <p className="text-zinc-600 text-[10px] mt-1">{p.frequency} — {p.route}</p>
                </div>
                {isSelected && (
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: p.color }}>
                    <Check className="w-3 h-3 text-black" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedPeptides.length > 0 && (
        <div className="bg-zinc-900/60 border border-zinc-800 p-5 mb-6" data-testid="manual-summary">
          <h3 className="text-white font-heading text-sm uppercase tracking-wider mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Your Custom Stack</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="text-left p-2 text-zinc-400 text-[10px] uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Peptide</th>
                  <th className="text-center p-2 text-zinc-400 text-[10px] uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Frequency</th>
                  <th className="text-center p-2 text-zinc-400 text-[10px] uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Route</th>
                </tr>
              </thead>
              <tbody>
                {selectedPeptides.map(p => (
                  <tr key={p.id} className="border-b border-zinc-800/50">
                    <td className="p-2 text-white flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />{p.name}
                    </td>
                    <td className="p-2 text-center text-zinc-300 text-xs">{p.frequency}</td>
                    <td className="p-2 text-center text-zinc-300 text-xs">{p.route}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="flex items-start gap-3 bg-[#f59e0b]/5 border border-[#f59e0b]/20 p-4">
        <AlertTriangle className="w-5 h-5 text-[#f59e0b] flex-shrink-0 mt-0.5" />
        <p className="text-zinc-300 text-xs"><strong className="text-[#f59e0b]">Disclaimer:</strong> This tool is for informational purposes only. Peptides are research compounds. Always consult a healthcare provider before starting any peptide protocol.</p>
      </div>
    </div>
  );
}
